import { cva } from "class-variance-authority";
import { useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";

const indicatorVariants = cva(
  "relative inline-flex size-4 shrink-0 touch-manipulation items-center justify-center rounded-xs text-[15px] leading-none normal-case tracking-normal outline-none before:absolute before:-inset-1 focus-visible:ring-2 focus-visible:ring-offset-1",
  {
    variants: {
      tone: {
        consumer: "text-electric focus-visible:ring-electric",
        pro: "text-lime-text focus-visible:ring-lime",
      },
    },
    defaultVariants: { tone: "consumer" },
  },
);

const tooltipVariants = cva(
  "z-[80] rounded-md px-2.5 py-1.5 text-[12px] font-medium leading-4 normal-case tracking-normal",
  {
    variants: {
      tone: {
        consumer: "bg-ink text-white",
        pro: "border border-pro-border bg-pro-card text-pro-text",
      },
    },
    defaultVariants: { tone: "consumer" },
  },
);

interface FieldLabelProps {
  children: string;
  htmlFor?: string;
  required?: boolean;
  tone?: "consumer" | "pro";
  className?: string;
}

function FieldLabel({
  children,
  htmlFor,
  required = false,
  tone = "consumer",
  className,
}: FieldLabelProps) {
  const Label = htmlFor ? "label" : "span";

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1", className)}>
      <Label htmlFor={htmlFor}>{children}</Label>
      {required && <RequiredFieldIndicator label={children} tone={tone} />}
    </span>
  );
}

function RequiredFieldIndicator({
  label,
  tone = "consumer",
}: {
  label: string;
  tone?: "consumer" | "pro";
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const container = triggerRef.current?.closest<HTMLElement>(
    '[role="dialog"], [data-pro-drawer], .pro-dark, .pro-light',
  );

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={`${label} is required`}
            className={indicatorVariants({ tone })}
            onClick={(event) => {
              event.preventDefault();
              setOpen(true);
            }}
          >
            <span aria-hidden="true">*</span>
          </button>
        </TooltipTrigger>
        <TooltipContent container={container} className={tooltipVariants({ tone })}>
          Required
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { FieldLabel, RequiredFieldIndicator };
